import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Html5Qrcode, Html5QrcodeCameraScanConfig } from "html5-qrcode";
import { MessageService } from "primeng/api";

@Component({
  selector: "app-qr-scanner",
  templateUrl: "./qr-scanner.component.html",
  styleUrls: ["./qr-scanner.component.css"],
})
export class QrScannerComponent implements OnInit, OnDestroy {
  public isScanning: boolean = false;

  @Output() scanSuccess = new EventEmitter<string>();
  @Output() onClose = new EventEmitter<void>();

  html5QrCode: Html5Qrcode | null = null;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.start();
  }

  ngOnDestroy() {
    this.stop();
  }

  start() {
    this.isScanning = true;

    // Check camera permission first
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then(() => this.initScanner())
      .catch(() => {
        this.showCameraError("CAMERA_PERMISSION_DENIED");
        this.isScanning = false;
      });
  }

  private initScanner() {
    // Create Html5Qrcode instance with native barcode detector enabled
    this.html5QrCode = new Html5Qrcode("qr-reader", {
      verbose: true,
      useBarCodeDetectorIfSupported: true,
    });

    Html5Qrcode.getCameras()
      .then((devices) => {
        if (!devices?.length) {
          this.showCameraError("CAMERA_NOT_FOUND");
          return;
        }

        const backCamera =
          devices.find((d) => d.label.toLowerCase().includes("back")) ||
          (devices.length > 1 ? devices[1] : devices[0]);

        const cameraId = backCamera.id;

        const cameraScanConfig: Html5QrcodeCameraScanConfig = {
          fps: 30, // smooth scanning
          qrbox: this.calculateQrBox(), // responsive qrbox
          videoConstraints: {
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };

        this.html5QrCode
          .start(
            cameraId,
            cameraScanConfig,
            (decodedText) => {
              this.scanSuccess.emit(decodedText);
              this.stop();
            },
            () => {
              // ignore frame decode errors
            }
          )
          .catch(() => this.showCameraError("CAMERA_ACCESS_ERROR"));
      })
      .catch(() => this.showCameraError("CAMERA_ACCESS_ERROR"));
  }

  private calculateQrBox(): { width: number; height: number } {
    // Dynamically calculate qrbox size for mobile and desktop
    const width = Math.min(window.innerWidth * 0.5, 400);
    const height = Math.min(window.innerHeight * 0.7, 400);
    return { width, height };
  }

  private showCameraError(detailKey: string) {
    this.messageService.add({
      severity: "error",
      summary: this.translate.instant("DOOR_SCAN_SOLUTION.SCAN_ERROR_SUMMARY"),
      detail: this.translate.instant(`DOOR_SCAN_SOLUTION.${detailKey}`),
    });
  }

  stop() {
    if (!this.html5QrCode) {
      this.isScanning = false;
      this.onClose.emit();
      return;
    }

    this.html5QrCode
      .stop()
      .then(() => this.html5QrCode?.clear())
      .catch(() => {
        this.messageService.add({
          severity: "error",
          summary: this.translate.instant(
            "DOOR_SCAN_SOLUTION.SCAN_ERROR_SUMMARY"
          ),
          detail: this.translate.instant(
            "DOOR_SCAN_SOLUTION.STOP_ERROR_DETAIL"
          ),
        });
      })
      .finally(() => {
        this.html5QrCode = null;
        this.isScanning = false;
        this.onClose.emit();
      });
  }
}
